@if (isLoading()) {
<app-skeleton-watch></app-skeleton-watch>
} @else {
@if (!isPlaying()) {
<app-paused-video class="h-100vh" (resumeVideo)="onResume()"></app-paused-video>
}
<div class="pb-3 pb-lg-8">
  <!-- streaming header -->
  <!-- The facts about the course, on one surface. They were three loose text
       runs floating on the page ground, which read as a caption for the video
       rather than as the course's own header. -->
  <div class="watch-bar d-flex flex-wrap align-items-center justify-content-between">
    <div class="watch-bar__facts d-flex flex-wrap align-items-center">
      <span class="watch-fact d-inline-flex align-items-center fs-7">
        <app-keenicon name="note" class="fs-2"></app-keenicon>
        <span><b>{{ course?.total_chapter || 0 }}</b> {{ "APP.LESSON" | translate }}</span>
      </span>
      <span class="watch-fact d-inline-flex align-items-center fs-7">
        <app-keenicon name="youtube" class="fs-2"></app-keenicon>
        <span><b>{{ course?.total_chapter_item || 0 }}</b> {{ "APP.VIDEOS" | translate }}</span>
      </span>
      <span class="watch-fact d-inline-flex align-items-center fs-7">
        <app-keenicon name="teacher" class="fs-2"></app-keenicon>
        <span>{{ "APP.TEACH_BY" | translate }} <b>{{ course?.tutor?.name || "-" }}</b></span>
      </span>
    </div>

    <!-- Watching is the primary action on this page. Pausing the course was a
         second filled indigo pill competing with it from the top-right corner;
         it is a real control, just not the one anybody came here for. -->
    <button class="btn btn-light-primary watch-bar__action d-inline-flex align-items-center" (click)="onPauseCourse()">
      <app-keenicon [name]="'abstract-5'" type="solid" class="fs-2"></app-keenicon>
      {{ "ONLINE_CLASS.PAUSE_CLASS" | translate }}
    </button>
  </div>
  <!-- chapter selector -->
  <!-- A course can legitimately have no video at all — a set of quizzes sold on
       its own. The player used to render regardless, so that student opened
       their course and got a black rectangle above an empty playlist, with the
       thing they actually bought below the fold. Hidden when there is nothing
       to play; the tabs underneath (description, files, แบบฝึกหัด) then get the
       full width. -->
  @if (courseChapterGroup().length) {
  <div class="row g-5 mt-6">
    <div class="col-12 col-xl-8 mb-5 mb-xl-0 mt-0">
      <app-video-player [playlist]="courseChapterGroup()" [currentChapter]="currentChapterSrc"
        [chapter_id]="currentChapter ? currentChapter.chapter_id! : null" (videoListener)="videoListener($event)"
        (videoEnded)="onVideoEnded()"></app-video-player>
    </div>
    <div class="col-12 col-xl-4 mt-0">
      <!-- The playlist. One accordion and one id: this markup nested a second
           `#kt_course_accordion` inside the first, so every `data-bs-parent`
           was pointing at a duplicate id. The "no lessons" branch that sat here
           was unreachable — the whole block is already inside a length check. -->
      <!-- The card the list sits in — the list itself is skin-level (`.lesson-group` /
     `.lesson-row` in styles.scss), shared with the course page. -->
<div class="card watch-playlist overflow-hidden">
        <div class="accordion" id="kt_course_accordion">
          @for (chapterGroup of courseChapterGroup(); track $index; let i = $index) {
          <div role="button" data-bs-toggle="collapse" [attr.data-bs-target]="`#kt_course_lesson_${i + 1}`"
            [attr.aria-controls]="`kt_course_lesson_${i + 1}`" [attr.aria-expanded]="isExpanded()![i]! || 'false'"
            class="lesson-group" [class.is-open]="isExpanded()![i]!" (click)="toggleCollapse(i)">
            <span class="lesson-group__chevron">
              <app-keenicon name="down" class="fs-3 rotate"></app-keenicon>
            </span>
            <span class="lesson-group__id">
              <span class="lesson-group__name">{{ chapterGroup.name }}</span>
              <span class="lesson-group__meta">
                <app-keenicon name="youtube" class="fs-5"></app-keenicon>
                {{ chapterGroup.course_chapters.length }} {{ "APP.VIDEOS" | translate }}
              </span>
            </span>
            <span class="lesson-group__time">{{ chapterGroup.duration_video | durationVideo: "HH:mm:ss"
              }}</span>
          </div>

          <div class="collapse fade course-chapter-expanded overflow-auto" [ngClass]="{
                    show:
                      currentChapter &&
                      chapterGroup.id === currentChapter!.course_chapter_group_id,
                  }" [id]="`kt_course_lesson_${i + 1}`" data-bs-parent="#kt_course_accordion">
            <div class="lesson-rows">
              @for (course_chapter of chapterGroup.course_chapters; track $index) {
              <div class="lesson-row"
                [class.is-current]="currentChapter && course_chapter.chapter_id === currentChapter!.chapter_id"
                [class.is-locked]="isChapterLocked(course_chapter)" (click)="
                     onSelectedChapter({
                       data: course_chapter,
                       currentTime: course_chapter.chapter.current_time,
                     })
                   ">
                <!-- A trial only unlocks the preview chapters. Clicking a locked
                     one already warns, but without a padlock the list looks
                     fully available and the warning reads as a bug. -->
                <span class="lesson-row__cue">
                  @if (currentChapter && course_chapter.chapter_id === currentChapter!.chapter_id) {
                  <app-keenicon name="to-right" class="fs-3"></app-keenicon>
                  } @else if (isChapterLocked(course_chapter)) {
                  <app-keenicon name="lock-2" class="fs-4" data-testid="chapter-locked"></app-keenicon>
                  }
                </span>
                <span class="lesson-row__name text-overflow-line-1">{{ course_chapter.chapter.name }}</span>
                <span class="lesson-row__time"
                  [ngClass]="{'fw-bold':currentChapter && course_chapter.chapter_id === currentChapter!.chapter_id}">{{
                  course_chapter.chapter.duration_video | durationVideo: "HH:mm:ss"
                  }}</span>
              </div>
              }
            </div>
          </div>
          }
        </div>
      </div>
    </div>
  </div>
  }

  <!-- course info -->
  <div class="card mt-5 min-h-350px">
    <div class="card-header card-header-stretch">
      <!-- <div class="card-title d-flex align-items-center"></div> -->
      <div class="card-toolbar m-0">
        <ul class="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0 fw-bold" role="tablist">
          <li class="nav-item" role="presentation">
            <a id="kt_course_description_tab"
              class="nav-link justify-content-center text-hover-gray-800 text-active-gray-800 active"
              data-bs-toggle="tab" role="tab" href="#kt_course_description" aria-selected="true">
              {{ "ONLINE_CLASS.ABOUT_COURSE" | translate }}
            </a>
          </li>
          <li class="nav-item" role="presentation">
            <a id="kt_course_pdf_tab" class="nav-link justify-content-center text-hover-gray-800 text-active-gray-800"
              data-bs-toggle="tab" role="tab" href="#kt_course_pdf" aria-selected="false" tabindex="-1">
              {{ "ONLINE_CLASS.PDF_FILE" | translate }}
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div class="card-body pt-9">
      <div class="tab-content">
        <div id="kt_course_description" class="card-body p-0 tab-pane fade active show" role="tabpanel"
          aria-labelledby="kt_course_description_tab">
          <section #courseTitle>
            <div class="d-flex align-items-center flex-wrap gap-2 mb-2">
              <h1 class="fw-bold me-3 my-1">{{ course.name }}</h1>

              <span class="badge badge-light-primary my-1 me-2">{{
                course.course_category_name
                }}</span>
              <!-- Trial locks most chapters (see `isLocked()`), and until now
                   the only sign of that was finding a padlock partway down the
                   playlist. Saying it up front at the title means a trial
                   student never wonders why a chapter won't open. -->
              @if (studentCourse?.is_trial) {
              <span class="badge badge-light-success my-1 me-2">{{ "APP.FREE_TRIAL" | translate }}</span>
              }
              <!-- <span class="badge badge-light-danger my-1">important</span> -->
            </div>
            <div class="row g-2 g-md-6">
              <div class="d-flex align-items-center gap-2 col-4 col-md-auto">
                <app-keenicon name="note" class="fs-2 text-gray-600"></app-keenicon>
                <span class="text-gray-600 d-block fs-5 fw-semibold">{{ course?.total_chapter || 0 }}
                  {{ "APP.LESSON" | translate }}</span>
              </div>
              @if (course?.course_file_count) {
              <div class="d-flex align-items-center gap-2 col-6 col-md-auto">
                <app-keenicon name="tablet-book" class="fs-2 text-gray-600"></app-keenicon>
                <span class="text-gray-600 d-block fs-5 fw-semibold">{{ "APP.PDF_FILE" | translate }}
                  {{course.course_file_count || 0}} {{'APP.FILE_AMOUNT' |
                  translate}}
                </span>
              </div>

              }
              <div class="d-flex align-items-center gap-2 col-12 col-md-auto">
                <app-keenicon name="youtube" class="fs-2 text-gray-600"></app-keenicon>
                <span class="text-gray-600 d-block fs-5 fw-semibold">{{ "ONLINE_CLASS.STUDY_TIME" | translate }}
                  {{ course?.duration_video | durationVideo }}
                </span>
              </div>

            </div>
          </section>

          <section #courseExplain>
            <p class="fs-2 fw-semibold mt-12">
              {{ "APP.DESCRIPTION" | translate }}
            </p>
            <div class="fs-4 fw-normal" [innerHTML]="course.description"></div>
          </section>
          <!-- The legacy exam accordion lived here. It is replaced by the quiz
               engine, which draws its questions from the shared bank rather
               than from a fixed paper — see app-course-quiz-list. -->
          <!-- TODO: app sidebar panel -->
          <!-- <section #hidden class="">
            <section #courseQuiz>
              <p class="fs-2 fw-semibold mt-12">
                {{ "QUIZ.TITLE" | translate }}
              </p>
              <app-course-quiz-list [courseId]="course.id"></app-course-quiz-list>
            </section>
          </section> -->
        </div>

        <div id="kt_course_pdf" class="card-body p-0 tab-pane fade" role="tabpanel"
          aria-labelledby="kt_course_pdf_tab">
          @if (courseFiles.length) {
          <app-course-file-item [courseFiles]="courseFiles"
            [isTrial]="!!studentCourse?.is_trial"></app-course-file-item>
          } @else {
          <div class="border-0 align-items-center text-center py-4 fs-5 text-muted">
            {{ "ONLINE_CLASS.NO_DOC" | translate }}
          </div>
          }
        </div>
      </div>
    </div>
  </div>
</div>
}